import { useLingui } from '@lingui/react';
import { useReleasesQuery } from 'generated/gql';
import { isoToLocale } from 'helpers/locale';
import { useAccount } from 'hooks/useAccount';
import { orderBy } from 'lodash';
import { useEffect, useMemo, useState } from 'react';

/**
 * Fetch release notes from the API for the active UI locale. Returns the raw
 * Apollo query result, so callers also get `loading`, `error`, `refetch`, etc.
 */
const useReleases = () => {
  const account = useAccount();
  const { i18n } = useLingui();

  // Drive the query off the live UI locale so switching languages refetches.
  const locale = isoToLocale(i18n.locale);

  // On first load `WithAuth` activates the account's locale in an effect that
  // runs *after* render, so `i18n.locale` is briefly the boot default. Wait
  // until it has caught up to the account locale before fetching, otherwise we
  // fire a throwaway request with the wrong locale. Once ready we stay
  // unlocked, so later language switches refetch immediately.
  const [isLocaleReady, setIsLocaleReady] = useState(false);
  useEffect(() => {
    if (account && locale === account.locale) {
      setIsLocaleReady(true);
    }
  }, [account, locale]);

  return useReleasesQuery({
    skip: !isLocaleReady,
    variables: { locale },
  });
};

/**
 * Release notes ordered newest first. The "Wat is er nieuw?" page and the
 * onboarding modal steps must share this order, so a clicked release index
 * maps to the matching step in the modal.
 */
export const useOrderedReleases = () => {
  const { data, ...rest } = useReleases();

  const releases = useMemo(() => orderBy(data?.releases ?? [], 'date', 'desc'), [data?.releases]);

  return { ...rest, releases };
};

export default useReleases;
